import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MatSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse, ApiService } from '../apiservice/api.service';

const waitEndpoint = 'athena-backend/waitforquery';

interface WaitForEndpointInput {
	queryid: string;
}

interface WaitForEndpointOutput {
	status?: 'running' | 'finished';
	error?: string;
}

const getPagedResultsEndpoint = 'athena-backend/getqueryresultpage';
const getAllResultsEndpoint = 'athena-backend/getallqueryresult';

interface GetResultsEndpointInput {
	jwttoken: string;
	pagenumber: number;
}

interface EndpointQuery {
	name: string;
	jwttoken: string;
}

export interface SubmitQueryOutput {
	jwttoken?: string;
	error?: string;
}

async function delay(ms: number): Promise<void> {
	return new Promise(resolve => setTimeout(resolve, ms));
}

@Injectable({
	providedIn: 'root'
})
export class QueryService {

	private queries: EndpointQuery[] = [];

	private spinnerTopRef = this.cdkSpinnerCreate();
	private _isQueryRunning: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


	constructor(private apiService: ApiService, private overlay: Overlay) {
		this.isQueryRunning.subscribe(
			(running: boolean) => {
				if (running) {
					this.showSpinner();
				} else {
					this.spinnerTopRef.hasAttached() ? this.stopSpinner() : null;
				}
			}
		);
	}

	async getData<T, I>(dataset: string, params?: I, pagesize?: number, page?: number): Promise<T[]> {
		let query: EndpointQuery = null;
		await this.buildQueryCache(dataset, params).then(result => query = result);

		try {
			let results;
			if (page && pagesize) {
				results = await this.apiService.get<GetResultsEndpointInput, T[]>(
					getPagedResultsEndpoint,
					{
						jwttoken: query.jwttoken,
						pagenumber: !!page ? page : 0,
						pagesize: !!pagesize ? pagesize : 0,
					}
				);
			} else if (page) {
				results = await this.apiService.get<GetResultsEndpointInput, T[]>(
					getPagedResultsEndpoint,
					{
						jwttoken: query.jwttoken,
						pagenumber: !!page ? page : 0
					}
				);
			} else {
				results = await this.apiService.get<GetResultsEndpointInput, T[]>(
					getAllResultsEndpoint,
					{
						jwttoken: query.jwttoken,
						pagenumber: !!page ? page : 0
					}
				);
			}
			this._isQueryRunning.next(false);
			return results;
		} catch (err) {
			this._isQueryRunning.next(false);
			throw Error(`Failed to retrieve query results for query with token ${query.jwttoken}`);
		}
	}

	async getRawData<T, I>(dataset: string, params?: I): Promise<Observable<ApiResponse<T[]>>> {
		let query: EndpointQuery = null;
		await this.buildQueryCache(dataset, params).then(result => query = result);

		try {
			let results: Observable<ApiResponse<T[]>>;
			const queryResult: Subject<ApiResponse<T[]>> = new Subject();
			let pageNr: number = 0;
			let resultLength: number = 50;


			// const x = of([pageNr]).pipe(map());

			// queryResult
			const x = of(new Array<number>(resultLength)).pipe(
				map(async () => {
					console.log('entering map');
					const requestResult: ApiResponse<T[]> = await this.apiService.get<GetResultsEndpointInput, T[]>(
						getPagedResultsEndpoint,
						{
							jwttoken: query.jwttoken,
							pagenumber: pageNr,
							pagesize: 1000,
						}
					);
					console.log('request result: ' + requestResult);
					resultLength = requestResult.data.length;
					queryResult.next(requestResult);
					console.log('new queryResult published: Page ' + pageNr);
					pageNr++;
				})
				// ,takeWhile(() => resultLength === 1000 && pageNr <= 50)
			);

			// do {
			// 	const requestResult: ApiResponse<T[]> = await this.apiService.get<GetResultsEndpointInput, T[]>(
			// 		getPagedResultsEndpoint,
			// 		{
			// 			jwttoken: query.jwttoken,
			// 			pagenumber: pageNr,
			// 			pagesize: 1000,
			// 		}
			// 	);
			// 	resultLength = requestResult.data.length;
			// 	queryResult.next(requestResult);
			// 	queryResult.subscribe(value => console.log(value));
			// 	results = queryResult.asObservable();
			// 	console.log('new queryResult published: Page ' + pageNr);
			// 	pageNr++;
			// } while (resultLength === 1000 && pageNr <= 50);

			this._isQueryRunning.next(false);
			console.log('returning');
			results = queryResult.asObservable();
			return results;
		} catch (err) {
			this._isQueryRunning.next(false);
			throw Error(`Failed to retrieve query results for query with token ${query.jwttoken}`);
		}
	}

	private async buildQueryCache<I>(dataset: string, params?: I): Promise<EndpointQuery> {
		if (this._isQueryRunning.value) {
			throw Error('Another query is already running');
		}

		this._isQueryRunning.next(true);

		// check if dataset is already queried
		const jwttoken = dataset + (params ? btoa(JSON.stringify(params)) : '');
		let query: EndpointQuery = this.queries.find(q => q.name === jwttoken);
		if (!query) {
			// Start new athena job
			let startJobRequest: ApiResponse<SubmitQueryOutput>;
			try {
				startJobRequest = await this.apiService.post<any, SubmitQueryOutput>(dataset);
			} catch (err) {
				this._isQueryRunning.next(false);
				throw Error(`Could not start retrieval for dataset ${dataset}. Does the dataset exist?`);
			}

			// and wait for the results
			try {
				do {
					const jobWaitResult = (await this.apiService.get<WaitForEndpointInput, WaitForEndpointOutput>(
						waitEndpoint,
						{
							jwttoken: startJobRequest.data.jwttoken
						}
					));

					if (jobWaitResult.data.status === 'finished') {
						break;
					}

					await delay(3000);
				} while (true);
			} catch (err) {
				this._isQueryRunning.next(false);
				throw Error(`Could not retrieve JobResultStatus for query ${startJobRequest.data}`);
			}

			// Finally, add index for this query to cache
			query = { name: jwttoken, jwttoken: startJobRequest.data.jwttoken };
			this.queries.push(query);
			return query;
		}
	}

	private cdkSpinnerCreate(): OverlayRef {
		return this.overlay.create({
			hasBackdrop: true,
			backdropClass: 'dark-backdrop',
			positionStrategy: this.overlay.position()
				.global()
				.centerHorizontally()
				.centerVertically()
		});
	}

	private showSpinner(): void {
		this.spinnerTopRef.attach(new ComponentPortal(MatSpinner));
	}

	private stopSpinner(): void {
		this.spinnerTopRef.detach();
	}

	public get isQueryRunning(): Observable<boolean> {
		return this._isQueryRunning.asObservable();
	}
}
